<template>
  <CForm @submit="submit">
    <CModal :title="$t(title)" @close="close">
      <template #body>
        <CSearchField v-model="formData.description" :label="$t('createJob.descriptionLabel')" :placeholder="$t(descriptionPlaceholder)" :active="bookingItems.length > 0" required class="u-mb-4">
          <CButton v-for="item in bookingItems" @click="selectBookingItem(item)">
            {{ item.shortDescription }}
          </CButton>
        </CSearchField>

        <CField v-if="type === JobType.COMMENT" v-model="formData.severity" :label="$t('comment.severity')" required class="u-mb-4">
          <template #default="bindings">
            <CSeverityPicker :modelValue="formData.severity" @update:modelValue="bindings['onUpdate:modelValue']" />
          </template>
        </CField>

        <CField type="radio" v-model="formData.executiveAgreement" :options="executiveAgreementOptions" :label="$t('createJob.executiveAgreement')" required>
          <template #option="{option}">
            <CItem :title="getExecutiveAgreementTitle(option)" :icon="getExecutiveAgreementIcon(option)" />
          </template>
        </CField>

        <CField type="radio" v-model="formData.orderStatus" :options="orderStatusOptions" :disabledOptions="disabledOrderStatusOptions" :label="$t('createJob.orderStatus')" required>
          <template #option="{option}">
            <CItem :title="option === OrderStatus.INVISIBLE ? $t('createJob.orderStatusHidden') : $t('createJob.orderStatusVisible')"
                   :icon="option === OrderStatus.INVISIBLE? 'eye-off' : 'minus-double'" />
          </template>
        </CField>
      </template>
    </CModal>
  </CForm>
</template>

<script setup lang="ts">
  import { computed, markRaw, reactive } from 'vue';
  import { useI18n } from 'vue-i18n';
  import BookingItem from '@/booking/models/BookingItem';
  import CModal from '@/general/components/CModal.vue';
  import CSeverityPicker from '@/general/components/CSeverityPicker.vue';
  import AuthService from '@/general/services/AuthService';
  import { BasicModalResult, MODAL_CANCELLED } from '@/general/utils/Modal';
  import CItem from '@/shared/components/CItem.vue';
  import CButton from '@/shared/components/buttons/CButton.vue';
  import CField from '@/shared/components/form/CField.vue';
  import CForm from '@/shared/components/form/CForm.vue';
  import CSearchField from '@/shared/components/form/CSearchField.vue';
  import Permission from '@/shared/constants/Permission';
  import JobClient from '../client/JobClient';
  import ExecutionStatus from '../constants/ExecutionStatus';
  import JobType from '../constants/JobType';
  import OrderStatus from '../constants/OrderStatus';
  import { JobId } from '../models/AbstractJob';
  import JobContext from '../models/JobContext';

  const props = withDefaults(defineProps<{
    type?: JobType;
    context: JobContext;
    bookingItems: Array<BookingItem>;
  }>(), {
    type: JobType.JOB,
  });

  const emit = defineEmits<{ (e: 'close', v: JobId | BasicModalResult): void }>();
  const i18n = useI18n();

  const hasAllJobPermissions = computed(() => {
    const user = AuthService.getCurrentUser();
    return user.canEditJobs() && user.hasPermission(Permission.CAN_APPROVE_EXECUTION);
  });

  const canChangeOrderStatus = computed(() => props.type === JobType.JOB && hasAllJobPermissions.value);

  const formData = reactive({
    description: '',
    severity: null,
    executiveAgreement: null,
    orderStatus: canChangeOrderStatus.value ? null : OrderStatus.INVISIBLE,
  });

  const title = computed(() => props.type === JobType.COMMENT ? 'createJob.commentTitle' : 'createJob.jobTitle');

  const descriptionPlaceholder = computed(() => props.type === JobType.COMMENT ? 'createJob.commentDescriptionPlaceholder' : 'createJob.jobDescriptionPlaceholder');

  const bookingItems = computed(() => {
    const query = formData.description.trim().toLowerCase();
    if (!query) return [];
    return props.bookingItems.filter(item => item.shortDescription.toLowerCase().includes(query) && item.shortDescription !== formData.description).map(markRaw);
  });

  function selectBookingItem(item: BookingItem) {
    formData.description = item.shortDescription;
  }

  const executiveAgreementOptions = computed(() => {
    if (hasAllJobPermissions.value) {
      return [ExecutionStatus.NOT_GIVEN, ExecutionStatus.DO_NOT_EXECUTE, ExecutionStatus.EXECUTE_DIAGNOSIS, ExecutionStatus.EXECUTE];
    } else {
      return [ExecutionStatus.NOT_GIVEN, ExecutionStatus.REQUESTED_DIAGNOSIS, ExecutionStatus.REQUESTED_EXECUTION];
    }
  });

  function getExecutiveAgreementTitle(option: ExecutionStatus) {
    switch (option) {
      case ExecutionStatus.EXECUTE:
      case ExecutionStatus.REQUESTED_EXECUTION:
        return i18n.t('createJob.execute');
      case ExecutionStatus.DO_NOT_EXECUTE:
        return i18n.t('createJob.doNotExecute');
      case ExecutionStatus.EXECUTE_DIAGNOSIS:
      case ExecutionStatus.REQUESTED_DIAGNOSIS:
        return i18n.t('createJob.executeDiagnosis');
      case ExecutionStatus.NOT_GIVEN:
        return i18n.t('createJob.executeNotGiven');
    }
  }

  function getExecutiveAgreementIcon(option: ExecutionStatus) {
    switch (option) {
      case ExecutionStatus.EXECUTE:
      case ExecutionStatus.REQUESTED_EXECUTION:
        return 'thumbs-up';
      case ExecutionStatus.DO_NOT_EXECUTE:
        return 'thumbs-down';
      case ExecutionStatus.EXECUTE_DIAGNOSIS:
      case ExecutionStatus.REQUESTED_DIAGNOSIS:
        return 'search-eye';
      case ExecutionStatus.NOT_GIVEN:
        return 'minus-double';
    }
  }

  const orderStatusOptions = computed(() => canChangeOrderStatus.value ? [OrderStatus.INVISIBLE, OrderStatus.NOT_GIVEN] : [OrderStatus.INVISIBLE]);

  const disabledOrderStatusOptions = computed(() => canChangeOrderStatus.value ? [] : orderStatusOptions.value);

  async function submit() {
    const id = await JobClient.create(formData, props.context.dossier.id);
    emit('close', id);
  }

  function close(): void {
    emit('close', MODAL_CANCELLED);
  }
</script>